/**
 * Amplitude Analytics Provider
 * Handles Amplitude event tracking using official @amplitude/analytics-browser SDK
 * Uses ad-blocker-resistant naming
 * Includes Session Replay functionality
 */

import { init, track, setUserId, Identify, identify, add } from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { createDebugLogger } from '../../../utils/debugLogger';

const logger = createDebugLogger('amplitude');

interface AmplitudeConfig {
    apiKey: string;
    sessionReplay?: {
        sampleRate?: number; // Percentage of sessions to record (0-100)
        enabled?: boolean; // Enable/disable session replay
    };
    autocapture?: {
        enabled?: boolean; // Enable/disable autocapture
        elementInteractions?: boolean; // Track element interactions (clicks, form submissions, etc.)
    };
}

let isInitialized = false;

/**
 * Initialize Amplitude Analytics
 */
export const initializeAmplitude = (config: AmplitudeConfig): void => {
    if (typeof window === 'undefined') {
        return;
    }

    // Check if already initialized
    if (isInitialized) {
        return;
    }

    try {
        // Configure autocapture settings
        const autocaptureConfig = config.autocapture || {};
        const autocaptureEnabled = autocaptureConfig.enabled !== false; // Default to enabled
        const elementInteractions = autocaptureConfig.elementInteractions !== false; // Default to enabled

        // Initialize Amplitude with the API key
        init(config.apiKey, {
            defaultTracking: {
                pageViews: false, // We'll track page views manually
                sessions: true,
                formInteractions: false,
                fileDownloads: false,
            },
            // Autocapture is built into Browser SDK 2.10.0+
            // See: https://amplitude.com/docs/sdks/analytics/browser/autocapture-plugin
            autocapture: autocaptureEnabled ? {
                elementInteractions: elementInteractions,
            } : undefined,
        });

        // Add Session Replay plugin if enabled
        const sessionReplayConfig = config.sessionReplay || {};
        const sessionReplayEnabled = sessionReplayConfig.enabled !== false; // Default to enabled
        const sampleRate = sessionReplayConfig.sampleRate !== undefined ? sessionReplayConfig.sampleRate : 100; // Default to 100% (can be overridden via env)

        if (sessionReplayEnabled) {
            try {
                const replay = sessionReplayPlugin({
                    sampleRate: sampleRate / 100, // Convert percentage to decimal (0-1)
                });
                add(replay);
            } catch (error) {
                console.warn('Amplitude Session Replay plugin failed to initialize:', error);
            }
        }

        isInitialized = true;
    } catch (error) {
        console.warn('Amplitude initialization failed:', error);
    }
};

/**
 * Track event with Amplitude
 */
export const trackAmplitudeEvent = (
    eventName: string,
    properties: Record<string, any> = {}
): void => {
    logger.track(eventName, properties);
    if (!isInitialized) {
        return;
    }

    try {
        // Transform properties to Amplitude format
        const amplitudeProperties: Record<string, any> = {};

        // Map common properties
        if (properties.value !== undefined) {
            amplitudeProperties.revenue = properties.value;
        }
        if (properties.currency) {
            amplitudeProperties.currency = properties.currency;
        }

        // For purchase events (conversion_completed only), ensure complete data
        const isPurchaseEvent = eventName === 'conversion_completed';
        
        if (isPurchaseEvent) {
            // Required purchase properties
            amplitudeProperties.revenue = properties.value !== undefined ? properties.value : 0;
            amplitudeProperties.currency = properties.currency || 'USD';
            if (properties.transaction_id) {
                amplitudeProperties.transaction_id = properties.transaction_id;
            }
            
            // Product/membership properties
            if (properties.membership_tier || properties.product_name) {
                amplitudeProperties.product_name = properties.membership_tier || properties.product_name;
            }
            if (properties.price_id || properties.product_id) {
                amplitudeProperties.product_id = properties.price_id || properties.product_id;
            }
            if (properties.billing_cycle) {
                amplitudeProperties.billing_cycle = properties.billing_cycle;
            }
            
            // Items array for ecommerce tracking (Amplitude supports items array)
            if (properties.items && Array.isArray(properties.items) && properties.items.length > 0) {
                // Format items for Amplitude ecommerce tracking
                amplitudeProperties.items = properties.items.map((item: any) => ({
                    product_id: item.item_id || item.id || properties.product_id || '',
                    product_name: item.item_name || item.name || properties.product_name || '',
                    category: item.item_category || item.category || 'membership',
                    price: item.price !== undefined ? item.price : 0,
                    quantity: item.quantity !== undefined ? item.quantity : 1,
                })).filter((item: any) => item.product_id); // Filter out invalid items
            } else {
                // Fallback: create items array from available data
                amplitudeProperties.items = [{
                    product_id: properties.product_id || properties.price_id || '',
                    product_name: properties.product_name || properties.membership_tier || 'Membership',
                    category: 'membership',
                    price: properties.value || 0,
                    quantity: 1,
                }];
            }
            
            // Discount/coupon info
            if (properties.coupon_id) {
                amplitudeProperties.coupon_id = properties.coupon_id;
            }
            if (properties.discount_amount_cents !== undefined) {
                amplitudeProperties.discount = properties.discount_amount_cents / 100;
            }
        } else {
            // For non-purchase events, use simpler mapping
            if (properties.membership_tier || properties.product_name) {
                amplitudeProperties.product_name = properties.membership_tier || properties.product_name;
            }
            if (properties.price_id || properties.product_id) {
                amplitudeProperties.product_id = properties.price_id || properties.product_id;
            }
            if (properties.billing_cycle) {
                amplitudeProperties.billing_cycle = properties.billing_cycle;
            }
        }

        // Map step properties
        if (properties.step !== undefined) {
            amplitudeProperties.step = properties.step;
            amplitudeProperties.step_name = properties.step_name || `step_${properties.step}`;
        }

        // Map error properties
        if (properties.error_message) {
            amplitudeProperties.error_message = properties.error_message;
        }
        if (properties.error_type) {
            amplitudeProperties.error_type = properties.error_type;
        }

        // Map transaction properties
        if (properties.transaction_id) {
            amplitudeProperties.transaction_id = properties.transaction_id;
        }

        // Add all other properties (Amplitude supports custom properties)
        Object.keys(properties).forEach((key) => {
            if (!amplitudeProperties[key] && !key.startsWith('_')) {
                amplitudeProperties[key] = properties[key];
            }
        });

        // Track the event using the official SDK
        track(eventName, amplitudeProperties);
    } catch (error) {
        console.warn('Amplitude tracking failed:', error);
    }
};

/**
 * Set user ID for Amplitude
 */
export const setAmplitudeUserId = (userId: string): void => {
    if (!isInitialized) {
        return;
    }

    try {
        setUserId(userId);
    } catch (error) {
        console.warn('Amplitude setUserId failed:', error);
    }
};

/**
 * Set user properties for Amplitude
 */
export const setAmplitudeUserProperties = (userProperties: Record<string, any>): void => {
    if (!isInitialized) {
        return;
    }

    try {
        // Create an Identify object and set user properties
        const identifyObj = new Identify();
        Object.keys(userProperties).forEach((key) => {
            identifyObj.set(key, userProperties[key]);
        });
        identify(identifyObj);
    } catch (error) {
        console.warn('Amplitude setUserProperties failed:', error);
    }
};

/**
 * Track page view with Amplitude
 */
export const trackAmplitudePageView = (name: string, properties: Record<string, any> = {}): void => {
    if (!isInitialized) {
        return;
    }

    try {
        track('page_view', {
            page_name: name,
            page_url: typeof window !== 'undefined' ? window.location.href : '',
            ...properties,
        });
    } catch (error) {
        console.warn('Amplitude page view failed:', error);
    }
};

/**
 * Create Amplitude plugin for analytics library
 */
export const createAmplitudePlugin = (config: AmplitudeConfig) => {
    // Parse session replay config from environment or use defaults
    const sessionReplaySampleRate = process.env.REACT_APP_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE 
        ? parseInt(process.env.REACT_APP_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE, 10)
        : undefined;
    const sessionReplayEnabled = process.env.REACT_APP_AMPLITUDE_SESSION_REPLAY_ENABLED !== 'false';

    // Parse autocapture config from environment or use defaults
    const autocaptureEnabled = process.env.REACT_APP_AMPLITUDE_AUTOCAPTURE_ENABLED !== 'false';
    const elementInteractionsEnabled = process.env.REACT_APP_AMPLITUDE_AUTOCAPTURE_ELEMENT_INTERACTIONS !== 'false';

    const fullConfig: AmplitudeConfig = {
        ...config,
        sessionReplay: {
            enabled: config.sessionReplay?.enabled !== undefined ? config.sessionReplay.enabled : sessionReplayEnabled,
            sampleRate: config.sessionReplay?.sampleRate !== undefined 
                ? config.sessionReplay.sampleRate 
                : (sessionReplaySampleRate !== undefined ? sessionReplaySampleRate : 100),
        },
        autocapture: {
            enabled: config.autocapture?.enabled !== undefined ? config.autocapture.enabled : autocaptureEnabled,
            elementInteractions: config.autocapture?.elementInteractions !== undefined 
                ? config.autocapture.elementInteractions 
                : elementInteractionsEnabled,
        },
    };

    return {
        name: 'amplitude',
        config: fullConfig,
        initialize: () => {
            initializeAmplitude(fullConfig);
        },
        track: ({ payload }: any) => {
            trackAmplitudeEvent(payload.event, payload.properties || {});
        },
        page: ({ payload }: any) => {
            trackAmplitudePageView(payload.name || 'Unknown', payload.properties || {});
        },
        identify: ({ userId, traits }: any) => {
            if (userId) {
                setAmplitudeUserId(userId);
            }
            if (traits && Object.keys(traits).length > 0) {
                setAmplitudeUserProperties(traits);
            }
        },
    };
};
